"use client";

import React, { useEffect, useRef, useState } from "react";
import { ArrowRight, Sparkles } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === "undefined" || typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function ContactD4PlumbingHeating() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: contentRef,
    isVisible: isContentVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  const {
    ref: ctaRef,
    isVisible: isCtaVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section data-section-id="240182" id="contact-d4-plumbing-heating" className="dark bg-background text-foreground relative min-h-[90vh] flex items-center justify-center overflow-hidden py-24 lg:py-32">
      {}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <div className="absolute inset-0 bg-[linear-gradient(to_right,var(--color-border)_1px,transparent_1px),linear-gradient(to_bottom,var(--color-border)_1px,transparent_1px)] bg-[size:4rem_4rem] [mask-image:radial-gradient(ellipse_80%_50%_at_50%_50%,#000_10%,transparent_100%)] opacity-10"></div>
        
        {}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/10 rounded-lg blur-[120px] -z-10 mix-blend-screen"></div>
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary/10 rounded-lg blur-[120px] -z-10 mix-blend-screen"></div>
      </div>

      <div className="container mx-auto px-4 md:px-8 relative z-10 max-w-5xl">
        <div className="flex flex-col items-center text-center space-y-8">
          
          {}
          <div ref={headerRef} className={`transition-all duration-1000 ease-out ${isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <Badge variant="outline" className="border-primary/30 bg-primary/20 text-primary backdrop-blur-md py-2 px-5 rounded-md text-sm font-mono tracking-wider flex items-center gap-2 shadow-md">
              <Sparkles className="size-4" />
              RGI & SEAI REGISTERED
            </Badge>
          </div>

          {}
          <div ref={contentRef} className={`space-y-6 transition-all duration-1000 delay-200 ease-out ${isContentVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <h1 className="text-5xl md:text-6xl lg:text-7xl font-bold tracking-tighter font-default leading-[1.1]">
              <span className="bg-gradient-to-r from-primary to-primary/70 bg-clip-text text-transparent drop-shadow-sm">
                Dublin Plumbing,
              </span>
              <br />
              Heating & Boiler Specialists.
            </h1>
            
            <h3 className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto font-default font-medium leading-relaxed">
              Plumbing, boiler and heat pump services across Dublin and Dublin 4. RGI registered and fully insured.
            </h3>
          </div>

          {}
          <div ref={ctaRef} className={`flex flex-col sm:flex-row items-center justify-center gap-4 pt-8 transition-all duration-1000 delay-400 ease-out w-full sm:w-auto ${isCtaVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <Link to="/ContactPage" className="w-full sm:w-auto">
              <Button size="lg" className="w-full sm:w-auto rounded-md bg-primary text-primary-foreground border-0 h-14 px-8 text-base font-bold shadow-lg hover:shadow-primary/40 transition-all duration-300 hover:scale-105">
                Get Free Quote
                <ArrowRight className="ml-2 size-5" />
              </Button>
            </Link>
            
            <Link to="/BoilerInstallationPage" className="w-full sm:w-auto">
              <Button variant="outline" size="lg" className="w-full sm:w-auto rounded-md h-14 px-8 text-base font-semibold border-border/50 bg-background/50 backdrop-blur-sm hover:bg-accent hover:text-accent-foreground transition-all duration-300">
                Our Services
              </Button>
            </Link>
          </div>

        </div>
      </div>
    </section>;
}